import React, { useMemo, useState } from 'react';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  useSensor,
  useSensors,
  MouseSensor,
  TouchSensor,
  DragOverlay,
} from '@dnd-kit/core';
import { rectSortingStrategy, SortableContext, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { createPortal } from 'react-dom';

import s from './index.module.css';

function ReorderableItem({ dndId, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: dndId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: 'none',
    userSelect: 'none',
    pointerEvents: isDragging ? 'none' : undefined,
    zIndex: isDragging ? 100 : undefined,
    opacity: isDragging ? 0.2 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
}

function DragOverlayItem({ children }) {
  return (
    <div
      style={{
        touchAction: 'none',
        userSelect: 'none',
        pointerEvents: 'none',
        boxShadow: '0 0 0 1px rgba(63, 63, 68, 0.05), 0px 15px 15px 0 rgba(34, 33, 81, 0.25)',
      }}
    >
      {children}
    </div>
  );
}

function ReorderableBlocks({ assets, onReorder, renderAsset }) {
  const dndAssetIds = useMemo(() => assets.map(({ id }) => String(id)), [assets]);
  const [draggingAssetId, setDraggingAssetId] = useState(null);
  const draggingAsset = useMemo(() => {
    const idx = dndAssetIds.indexOf(draggingAssetId);
    if (idx === -1) {
      return null;
    }
    return assets[idx];
  }, [dndAssetIds, draggingAssetId, assets]);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event) {
    setDraggingAssetId(null);

    const { active, over } = event;
    if (!(active && over)) {
      return;
    }

    if (active.id !== over.id) {
      const oldIndex = dndAssetIds.indexOf(active.id);
      const newIndex = dndAssetIds.indexOf(over.id);
      onReorder(oldIndex, newIndex);
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      onDragStart={({ active }) => {
        if (!active) {
          return;
        }
        setDraggingAssetId(active.id);
      }}
      onDragCancel={() => {
        setDraggingAssetId(null);
      }}
    >
      <SortableContext items={dndAssetIds} strategy={rectSortingStrategy}>
        {assets.map((asset) => (
          <ReorderableItem key={asset.id} dndId={String(asset.id)}>
            {renderAsset(asset)}
          </ReorderableItem>
        ))}
      </SortableContext>

      {createPortal(
        <DragOverlay adjustScale={false} className={s.dragOverlay}>
          {draggingAssetId ? (
            <DragOverlayItem key={draggingAsset.id} id={draggingAsset.id}>
              {renderAsset(draggingAsset)}
            </DragOverlayItem>
          ) : null}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
}

export default ReorderableBlocks;
